import { ChangeEvent, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "./app/hooks"
import { forgotPassword } from './features/user/userSlice'
import {
    Backdrop,
    Button,
    CircularProgress,
    Divider,
    Grid,
    TextField,
    Typography,
} from "@mui/material";

import { Localized, useLocalization } from "@fluent/react"
import { showError } from "./features/notifications/notificationsSlice";
import { AuthPageLayout } from "./AuthPageLayout";
import { LanguageSelector } from "./LanguageSelector";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';

const PREFIX = 'ForgotPassword';

const classes = {
    backdrop: `${PREFIX}-backdrop`
};

export const ForgotPassword = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { l10n } = useLocalization()
    const [email, setEmail] = useState("");
    const [mask, setMask] = useState<boolean>(false);
    const [resetLinkSent, setResetLinkSent] = useState<boolean>(false);

    const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)

    const handleBackToLogin = () => {
        navigate("/login")
    }

    const handleResetClick = async () => {
        if (email) {
            try {
                setMask(true)
                const resultAction = await dispatch(forgotPassword({ email }))

                if (forgotPassword.fulfilled.match(resultAction)) {
                    setResetLinkSent(true)
                }
                if (forgotPassword.rejected.match(resultAction)) {
                    const { payload } = resultAction
                    if (payload) {
                        switch (payload.kind) {
                            case "http":
                                if (payload.body) {
                                    dispatch(showError(payload.body))
                                }
                                else {
                                    dispatch(showError("error"))
                                } break
                            case "connection":
                                dispatch(showError("connection-issue"))
                                break
                        }
                    } else {
                        dispatch(showError("error"))
                    }
                }
            }
            catch (error) {
                dispatch(showError(error ?? "error-login-failed"))
            }
            finally {
                setMask(false)
            }
        }
    }

    switch (resetLinkSent) {
        case true:
            return (
                <AuthPageLayout>
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                <Localized id="forgot-password-link-sent">
                                    <span>forgot-password-link-sent</span>
                                </Localized>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ mx: 2, mt: 1, mb: 1 }} />
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: 'center', marginTop: 4 }}>
                            <Button
                                onClick={handleBackToLogin}
                                color="primary"
                                size="small"
                                startIcon={<ArrowLeftIcon />}
                                sx={{ textTransform: "none" }}
                            >
                                <Localized id="back-to-login">
                                    <span>_Powrót do logowania</span>
                                </Localized>
                            </Button>
                        </Grid>
                        <Grid item textAlign="center">
                            <LanguageSelector />
                        </Grid>
                    </Grid>
                </AuthPageLayout>
            )
        default:
            return (
                <AuthPageLayout>
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight="bold" textAlign="center">
                                <Localized id="forgot-password-question">
                                    <span>Zapomniałeś hasła?</span>
                                </Localized>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                <Localized id="forgot-password-description">
                                    <span>Zapomniałeś hasła?</span>
                                </Localized>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                value={email}
                                sx={{ width: '80%' }}
                                onChange={onEmailChange}
                                label={l10n.getString("user-email", null, "Adres email")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button onClick={handleResetClick} color="primary" size="large" variant="contained">
                                <Localized id="reset-password">
                                    <span>reset</span>
                                </Localized>
                            </Button>
                        </Grid>
                        <Divider style={{ marginLeft: 30, marginRight: 20, marginTop: 20 }} />
                        <Grid item xs={12} style={{ textAlign: 'center', marginTop: 10, marginRight: 10 }}>
                            <Button
                                onClick={handleBackToLogin}
                                color="primary"
                                size="small"
                                startIcon={<ArrowLeftIcon />}
                                sx={{ textTransform: "none", marginLeft: 3 }}
                            >
                                <Localized id="back-to-login">
                                    <span>_Powrót do logowania</span>
                                </Localized>
                            </Button>
                        </Grid>

                        <Grid item textAlign="center">
                            <LanguageSelector />
                        </Grid>
                    </Grid>
                    <Backdrop className={classes.backdrop} open={mask}>
                        <CircularProgress color="inherit"></CircularProgress>
                    </Backdrop>
                </AuthPageLayout>
            );
    }
}